import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import "./RegisterStyle.css";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const [verificationStatus, setVerificationStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      // No token means user wants to resend verification email
      setVerificationStatus("resend");
      // Try to get email from localStorage if available
      const storedEmail = localStorage.getItem("pendingVerificationEmail");
      if (storedEmail) {
        setUserEmail(storedEmail);
      }
      return;
    }

    verifyEmailToken(token);
  }, [searchParams]);

  const verifyEmailToken = async (token) => {
    try {
      const response = await api.get(`/auth/verify-email/${token}`);

      if (response.data.status === "success") {
        setVerificationStatus("success");
        setMessage(response.data.message);

        // Store the token and user data
        if (response.data.data.token) {
          localStorage.setItem("token", response.data.data.token);
        }

        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate("/dashboard");
        }, 3000);
      }
    } catch (error) {
      setVerificationStatus("error");
      if (error.response?.data?.message) {
        setMessage(error.response.data.message);
      } else {
        setMessage("Failed to verify email. The link may have expired.");
      }

      // Try to extract email from error or storage
      const email = error.response?.data?.email || localStorage.getItem("pendingVerificationEmail");
      if (email) {
        setUserEmail(email);
      }
    }
  };

  const handleResendEmail = async () => {
    if (!userEmail) {
      setResendMessage("Please enter your email address");
      return;
    }

    setResendLoading(true);
    setResendMessage("");

    try {
      const response = await api.post("/auth/resend-verification", { email: userEmail });
      setResendMessage(response.data.message || "Verification email sent! Please check your inbox.");
    } catch (error) {
      setResendMessage(error.response?.data?.message || "Failed to resend email. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="verify-email-container-main">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-8 col-xl-7">
            <div className="verify-email-card verify-email-card-shadow verify-email-rounded-modern verify-email-fade-in">

              {/* Verifying State */}
              {verificationStatus === "verifying" && (
                <div className="verify-email-content text-center p-5">
                  <div className="verify-email-spinner-wrapper mb-4">
                    <div className="spinner-border verify-email-spinner" role="status">
                      <span className="visually-hidden">Verifying...</span>
                    </div>
                  </div>
                  <h2 className="verify-email-title mb-3">
                    <i className="fa fa-envelope-open verify-email-icon me-2"></i>
                    Verifying Your Email
                  </h2>
                  <p className="verify-email-subtitle text-muted">
                    Please wait while we verify your email address...
                  </p>
                </div>
              )}

              {/* Success State */}
              {verificationStatus === "success" && (
                <div className="verify-email-content text-center p-5">
                  <div className="verify-email-success-icon-wrapper mb-4">
                    <i className="fa fa-check-circle verify-email-success-icon"></i>
                  </div>
                  <h2 className="verify-email-title mb-3">
                    Email Verified Successfully!
                  </h2>
                  <p className="verify-email-subtitle text-muted mb-4">
                    {message}
                  </p>
                  <div className="verify-email-success-box mb-4">
                    <p className="mb-2">
                      <i className="fa fa-check text-success me-2"></i>
                      Your account is now active
                    </p>
                    <p className="mb-0">
                      <i className="fa fa-check text-success me-2"></i>
                      Redirecting to dashboard...
                    </p>
                  </div>
                  <div className="verify-email-redirect-message">
                    <div className="spinner-border spinner-border-sm me-2"></div>
                    Taking you to your dashboard
                  </div>
                </div>
              )}

              {/* Resend State */}
              {verificationStatus === "resend" && (
                <div className="verify-email-content p-5">
                  <div className="text-center mb-4">
                    <div className="verify-email-resend-icon-wrapper mb-4">
                      <i className="fa fa-paper-plane verify-email-resend-icon"></i>
                    </div>
                    <h2 className="verify-email-title mb-3">
                      Resend Verification Email
                    </h2>
                    <p className="verify-email-subtitle text-muted mb-4">
                      Enter your email address and we'll send you a new verification link.
                    </p>
                  </div>

                  {/* Resend Email Form */}
                  <div className="verify-email-resend-section">
                    <div className="mb-3">
                      <label className="verify-email-form-label fw-semibold">
                        <i className="fa fa-envelope text-muted me-2"></i>
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control verify-email-input-modern"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        placeholder="your@email.com"
                      />
                    </div>

                    {resendMessage && (
                      <div className={`verify-email-alert mb-3 ${resendMessage.includes("sent") || resendMessage.includes("success") ? "verify-email-alert-success" : "verify-email-alert-danger"}`}>
                        <i className={`fa ${resendMessage.includes("sent") || resendMessage.includes("success") ? "fa-check-circle" : "fa-exclamation-circle"} me-2`}></i>
                        <span>{resendMessage}</span>
                      </div>
                    )}

                    <button
                      onClick={handleResendEmail}
                      disabled={resendLoading || !userEmail}
                      className="btn verify-email-btn-primary w-100 mb-3"
                    >
                      {resendLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-paper-plane me-2"></i>
                          Send Verification Email
                        </>
                      )}
                    </button>

                    <div className="text-center">
                      <Link to="/login" className="verify-email-link">
                        <i className="fa fa-arrow-left me-2"></i>
                        Back to Login
                      </Link>
                    </div>
                  </div>

                  {/* Help Section */}
                  <div className="verify-email-help-section mt-4">
                    <p className="text-muted small text-center mb-2">
                      <i className="fa fa-info-circle me-1"></i>
                      Helpful Tips:
                    </p>
                    <ul className="verify-email-help-list">
                      <li>Check your spam or junk folder</li>
                      <li>Make sure you're using the correct email address</li>
                      <li>Verification links expire after 24 hours</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Error State */}
              {verificationStatus === "error" && (
                <div className="verify-email-content p-5">
                  <div className="text-center mb-4">
                    <div className="verify-email-error-icon-wrapper mb-4">
                      <i className="fa fa-times-circle verify-email-error-icon"></i>
                    </div>
                    <h2 className="verify-email-title mb-3">
                      Verification Failed
                    </h2>
                    <p className="verify-email-subtitle text-muted mb-4">
                      {message}
                    </p>
                  </div>

                  {/* Resend Email Section */}
                  <div className="verify-email-resend-section">
                    <h5 className="text-center mb-3">
                      <i className="fa fa-paper-plane me-2"></i>
                      Resend Verification Email
                    </h5>

                    <div className="mb-3">
                      <label className="verify-email-form-label fw-semibold">
                        <i className="fa fa-envelope text-muted me-2"></i>
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control verify-email-input-modern"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        placeholder="your@email.com"
                      />
                    </div>

                    {resendMessage && (
                      <div className={`verify-email-alert mb-3 ${resendMessage.includes("sent") || resendMessage.includes("success") ? "verify-email-alert-success" : "verify-email-alert-danger"}`}>
                        <i className={`fa ${resendMessage.includes("sent") || resendMessage.includes("success") ? "fa-check-circle" : "fa-exclamation-circle"} me-2`}></i>
                        <span>{resendMessage}</span>
                      </div>
                    )}

                    <button
                      onClick={handleResendEmail}
                      disabled={resendLoading || !userEmail}
                      className="btn verify-email-btn-primary w-100 mb-3"
                    >
                      {resendLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-paper-plane me-2"></i>
                          Resend Verification Email
                        </>
                      )}
                    </button>

                    <div className="text-center">
                      <Link to="/login" className="verify-email-link">
                        <i className="fa fa-arrow-left me-2"></i>
                        Back to Login
                      </Link>
                    </div>
                  </div>

                  {/* Help Section */}
                  <div className="verify-email-help-section mt-4">
                    <p className="text-muted small text-center mb-2">
                      <i className="fa fa-info-circle me-1"></i>
                      Need help? Here are some tips:
                    </p>
                    <ul className="verify-email-help-list">
                      <li>Check your spam or junk folder</li>
                      <li>Make sure you're using the latest verification email</li>
                      <li>Verification links expire after 24 hours</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
